import { useI18n } from '@affine/i18n';
import { SignOutIcon } from '@blocksuite/icons/rc';
import type { JSX, ReactNode } from 'react';

import { Avatar } from '../../ui/avatar';
import { Button, IconButton } from '../../ui/button';
import type { User } from '../auth-components';
import { BrandScreen } from '../page-detail-skeleton';
import { info, largeButtonEffect, wrapper } from './styles.css';

export interface NotFoundPageProps {
  user?: User | null;
  signInComponent?: JSX.Element;
  onBack: () => void;
  onSignOut: () => void;
  /** Optional custom back button (e.g. RevealButton from sign-in) */
  renderBackButton?: (label: string, onClick: () => void) => ReactNode;
}

const DefaultBackButton = ({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) => (
  <Button
    variant="primary"
    size="extraLarge"
    onClick={onClick}
    className={largeButtonEffect}
  >
    {label}
  </Button>
);

export const NoPermissionOrNotFound = ({
  user,
  onBack,
  onSignOut,
  signInComponent,
  renderBackButton,
}: NotFoundPageProps) => {
  const t = useI18n();
  const backLabel = t['404.back']();

  if (!user) {
    return signInComponent ?? null;
  }

  return (
    <BrandScreen>
      <div className={info} data-testid="not-found">
        <p className={wrapper}>{t['404.hint']()}</p>
        <div className={wrapper}>
          {renderBackButton ? (
            renderBackButton(backLabel, onBack)
          ) : (
            <DefaultBackButton label={backLabel} onClick={onBack} />
          )}
        </div>
        <div className={wrapper}>
          <Avatar url={user.avatar ?? user.image} name={user.label} />
          <span style={{ margin: '0 12px' }}>{user.email}</span>
          <IconButton
            onClick={onSignOut}
            size="20"
            tooltip={t['404.signOut']()}
          >
            <SignOutIcon />
          </IconButton>
        </div>
      </div>
    </BrandScreen>
  );
};

export const NotFoundPage = ({
  user,
  onBack,
  onSignOut,
  renderBackButton,
}: NotFoundPageProps) => {
  const t = useI18n();
  const backLabel = t['404.back']();

  return (
    <BrandScreen>
      <div className={info} data-testid="not-found">
        <p className={wrapper}>{t['404.hint']()}</p>
        <div className={wrapper}>
          {renderBackButton ? (
            renderBackButton(backLabel, onBack)
          ) : (
            <DefaultBackButton label={backLabel} onClick={onBack} />
          )}
        </div>
        {user ? (
          <div className={wrapper}>
            <Avatar url={user.avatar ?? user.image} name={user.label} />
            <span style={{ margin: '0 12px' }}>{user.email}</span>
            <IconButton
              onClick={onSignOut}
              size="20"
              tooltip={t['404.signOut']()}
            >
              <SignOutIcon />
            </IconButton>
          </div>
        ) : null}
      </div>
    </BrandScreen>
  );
};
